import { useMonarch } from '@redventures/cohesion-utils-react';
import { useMemo } from 'react';

type ChatType = 'Giga' | 'Proactive' | 'ProactiveV2';

interface IdleTriggerConfig {
  timeout: number; // in seconds
}

interface ThresholdTriggerConfig {
  threshold: number; // number of actions before trigger fires
}

interface FormErrorTriggerConfig {
  forceModal?: boolean; // if true, always show proactive modal for form-error triggers (Giga and Proactive)
}

interface BackButtonTriggerConfig {
  enabled?: boolean; // if false, back button trigger will be disabled for both Giga and Proactive chat
}

interface ChatbotTriggers {
  idle?: IdleTriggerConfig;
  vasUncertainty?: ThresholdTriggerConfig;
  productUncertainty?: ThresholdTriggerConfig;
  formError?: FormErrorTriggerConfig;
  backButton?: BackButtonTriggerConfig;
}

interface PositionConfig {
  bottom?: string; // Distance from bottom (e.g., "50px")
  right?: string; // Distance from right (e.g., "40px")
}

interface ProactiveConfig {
  message?: string; // Proactive message text (e.g., "Need Help?")
  type?: 'default' | 'side_rail'; // Proactive display type
  display?: 'horizontal' | 'vertical'; // Side rail orientation (required when type is "side_rail")
}

interface AnimateConfig {
  message: string; // Animation message text (e.g., "Need help?")
}

export interface UseChatbotMonarchReturnType {
  chatType?: ChatType;
  isLoading: boolean;
  sdkVersion: string;
  triggers?: ChatbotTriggers;
  position?: PositionConfig;
  proactive?: ProactiveConfig;
  animate?: AnimateConfig;
  triggerType?: 'proactive'; // SDK trigger type
}

interface CartChatbotNewRuleGiga {
  sdkVersion: string;
  chatType: ChatType;
  triggers?: ChatbotTriggers;
  position?: PositionConfig;
  proactive?: ProactiveConfig;
  animate?: AnimateConfig;
  triggerType?: 'proactive';
}

const DEFAULT_FEATURE_FLAGS: CartChatbotNewRuleGiga = {
  chatType: undefined,
  sdkVersion: 'CB-20251113.01',
  triggers: undefined,
  position: undefined,
  proactive: undefined,
  animate: undefined,
  triggerType: undefined,
};

const useChatbotMonarch = (): UseChatbotMonarchReturnType => {
  const { result, isLoading, err } = useMonarch();

  const flags = useMemo(() => {
    // Wait until loading is complete before processing flags
    if (isLoading || err) {
      return {
        ...DEFAULT_FEATURE_FLAGS,
        isLoading: true,
      };
    }

    // Merge Monarch rule data with defaults for simplicity
    const ruleData = (result?.source?.cartChatbot?.value ?? {}) as CartChatbotNewRuleGiga;

    return {
      ...DEFAULT_FEATURE_FLAGS,
      ...ruleData,
      isLoading: false,
    } as UseChatbotMonarchReturnType;
  }, [result, isLoading]);

  return flags;
};

export default useChatbotMonarch;
